import * as React from "react"
import { Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer"
import dayjs from "dayjs"
import type { TaskCompletionDates } from "@src/generatedClient/generated/taskreportsApi"

// Define styles for the PDF
const styles = StyleSheet.create({
  landscapePage: {
    padding: 30,
    backgroundColor: "#FFFFFF",
    width: "100%",
  },
  header: {
    marginBottom: 20,
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  reportTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#1E293B",
  },
  noActivityMessage: {
    fontSize: 14,
    textAlign: "center",
    marginTop: 40,
    fontStyle: "italic",
  },
  icon: {
    width: 12,
    height: 12,
  },
  // Activity Grid styles
  gridContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  monthTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1E293B",
    marginBottom: 15,
  },
  gridHeader: {
    flexDirection: "row",
    border: "1px solid #E2E8F0",
    borderBottomWidth: 0.5,
    paddingTop: 8,
    paddingBottom: 8,
  },
  gridHeaderCell: {
    width: 30,
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
    color: "#475569",
  },
  gridTaskColumn: {
    width: 150,
    fontSize: 11,
    fontWeight: "bold",
    color: "#475569",
    paddingLeft: 5,
  },
  gridRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#E2E8F0",
    borderTopWidth: 0.5,
    borderTopColor: "#E2E8F0",
    minHeight: 30,
    alignItems: "center",
    paddingVertical: 5,
    borderLeft: "1px solid #E2E8F0",
    borderRight: "1px solid #E2E8F0",
  },
  gridRowEven: {
    backgroundColor: "#F8FAFC",
  },
  gridTaskCell: {
    width: 150,
    fontSize: 10,
    color: "#334155",
    paddingRight: 5,
    paddingLeft: 5,
  },
  gridCell: {
    width: 30,
    height: 30,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  gridRowLast: {
    borderBottomWidth: 1,
  },
  statusIndicator: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginRight: 5,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
})

// Status indicator SVG icons
function CheckIcon() {
  return (
    <Svg viewBox="0 0 24 24" style={styles.icon}>
      <Path
        d="M20 6L9 17l-5-5"
        stroke="#10B981"
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

function XIcon() {
  return (
    <Svg viewBox="0 0 24 24" style={styles.icon}>
      <Path
        d="M6 6l12 12M6 18L18 6"
        stroke="#EF4444"
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

export function ActivityGridPdf(props: {
  month: string | Date
  completions: TaskCompletionDates[]
}) {
  const { month, completions } = props

  const monthDate = typeof month === "string" ? dayjs(month) : dayjs(month)
  const monthKey = monthDate.format("YYYY-MM")
  const daysInMonth = monthDate.daysInMonth()
  const monthName = monthDate.format("MMMM YYYY")

  const monthCompletions = React.useMemo(() =>  completions.filter((completion) => {
      const completionDate = dayjs(completion.startDate)
      return completionDate.format("YYYY-MM") === monthKey
    })
  , [completions, monthKey])

  const tasks = React.useMemo(() =>  Array.from(new Set(monthCompletions.map((c) => c.taskName))), [monthCompletions])

  const completionMap = React.useMemo(() => {
    const map = new Map<string, Map<number, boolean | null>>()

    tasks.forEach((task) => {
      map.set(task, new Map())

      for (let day = 1; day <= daysInMonth; day++) {
        map.get(task)?.set(day, null)
      }
    })

    monthCompletions.forEach((completion) => {
      const day = dayjs(completion.startDate).date()
      const taskMap = map.get(completion.taskName)
      if (taskMap) {
        taskMap.set(day, completion.completed)
      }
    })

    return map
  }, [tasks, daysInMonth, monthCompletions])

  if (monthCompletions.length === 0) {
    return (
      <Page size="A4" orientation="landscape" style={styles.landscapePage}>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View>
              <Text style={styles.reportTitle}>Activity Grid Report</Text>
            </View>
          </View>
        </View>
        <Text style={styles.monthTitle}>{monthName}</Text>
        <Text style={styles.noActivityMessage}>No task completions found for this month.</Text>
      </Page>
    )
  }

  return (
    <Page size="A4" orientation="landscape" style={styles.landscapePage}>
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View>
            <Text style={styles.reportTitle}>Activity Grid Report</Text>
          </View>
        </View>
      </View>

      <Text style={styles.monthTitle}>{monthName}</Text>

      <View style={styles.gridContainer}>
        <View style={styles.gridHeader}>
          <Text style={styles.gridTaskColumn}> </Text>
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
            <Text key={day} style={styles.gridHeaderCell}>
              {day}
            </Text>
          ))}
        </View>

        {tasks.map((task, taskIndex) => {
          const taskCompletions = completionMap.get(task)

          return (
            <View key={task} wrap={false} style={[styles.gridRow, taskIndex % 2 === 1 ? styles.gridRowEven : {}, taskIndex === completions.length - 1 ? styles.gridRowLast : {}]}>
              <Text style={styles.gridTaskCell}>{task}</Text>

              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const status = taskCompletions?.get(day)

                if (status === null) {
                  return <View key={day} style={styles.gridCell} />
                }

                if (status === true) {
                  return (
                    <View key={day} style={styles.gridCell}>
                      <View style={[styles.statusIndicator]}>
                        <CheckIcon />
                      </View>
                    </View>
                  )
                }

                return (
                  <View key={day} style={styles.gridCell}>
                    <View style={[styles.statusIndicator]}>
                      <XIcon />
                    </View>
                  </View>
                )
              })}
            </View>
          )
        })}
      </View>
    </Page>
  )
}
